// components/source/sourceSlashCommands.ts
//
// The curated "/" menu for the Source composer.
//
// Deliberately NOT built from COMMAND_DEFINITIONS: that holds ~100 internal agent actions, and
// showing them raw would be a debug panel, not a menu. This is the short list of things a leasing
// manager actually wants to start by name.
//
// A command here is a NAME, not a script. Picking one types "/refine " into the composer and
// stops, so the user can add their own context ("/refine but keep La Caverne top three") before
// sending. The procedure each command implies lives in the agent's knowledge base, not in the
// message: pasting a 300-word instruction into the transcript as if the user had written it made
// the chat unreadable and told them nothing they did not already know.
export interface SourceSlashCommand {
  /** What the user types after "/". */
  name: string;
  /**
   * A short label for the command. Optional because only some entries carry one, and nothing
   * renders it yet: the menu shows `name` and `hint`. Kept because it is written copy, and
   * deleting it would mean rewriting it when the menu grows a two-line row.
   */
  title?: string;
  hint: string;
  /**
   * Which half of the menu this belongs to.
   *
   * 'source' is what WE wrote for this module - a name for a piece of work a leasing manager asks
   * for. 'agent' is the action registry the agent itself runs on. They are not the same kind of
   * thing and a flat list of both reads as one undifferentiated wall, which is why they are
   * separated. Absent means 'source': the curated list below is the default half.
   */
  group?: 'source' | 'agent' | 'list' | 'plan';
}

export const SOURCE_SLASH_COMMANDS: SourceSlashCommand[] = [
  {
    name: 'refine',
    hint: 'Critique and fix the list until it holds up',
  },
  {
    name: 'fetch',
    title: 'Fetch the data',
    hint: 'Go and get it, and never come back empty',
  },
  {
    name: 'prove',
    title: 'Prove what is on screen',
    hint: 'Sources and links behind every figure shown',
  },
  {
    name: 'interesting',
    hint: 'Re-read the whole list and say who to call first',
  },
  {
    name: 'fill-stores',
    title: 'Fill the missing stores',
    hint: "Read the brand's own locator and add every store we lack",
  },
  {
    name: 'verify-stores',
    hint: 'Check every claimed store of the open brand really exists',
  },
  {
    name: 'fill-finances',
    title: 'Fill the official finances',
    hint: "Research the brand's official figures, write them, and show them on the panel",
  },
  {
    name: 'fill-data',
    title: 'Rebuild the brand card',
    hint: 'Runs every fill below: re-research each section, compare with what we hold, keep what is proven',
  },
  {
    name: 'fill-identity',
    title: 'Fill the summary',
    hint: 'Website, logo, positioning, category, surface and network size, re-researched',
  },
  {
    name: 'fill-legal',
    title: 'Fill the legal entity',
    hint: 'The company behind the brand, anchored in the register',
  },
  {
    name: 'fill-expansion',
    title: 'Check the expansion chart',
    hint: 'Openings and closures year by year against public sources',
  },
  {
    name: 'fill-contacts',
    title: 'Fill the contacts',
    hint: 'Verify the contacts we hold, or find them (billed per contact)',
  },
  {
    name: 'check-data',
    title: 'Check and repair the data',
    hint: 'Audit the open brand\'s record, then fix what is wrong',
  },
  {
    name: 'export',
    title: 'Export the shortlist',
    hint: 'A PDF you can send, with the scores, the reasons and the sources',
  },
  {
    name: 'params',
    title: 'Save or load the parameters',
    hint: 'Reuse your usual launch setup on this campaign',
  },
  {
    name: 'extend',
    title: 'Add more brands to this list',
    hint: 'Grow the shortlist on the same rubric; actual spend varies with analysis cost',
  },
  {
    // The odd one out, and deliberately so: every other entry here names a piece of WORK for the
    // agent to do on the shortlist. This one changes the app's own language, so the agent executes
    // SET_LANGUAGE rather than reasoning about a list. It earns its place in the same menu because
    // this is where a user looks for "how do I tell it to do X".
    name: 'language',
    title: 'Change the language',
    hint: 'Switch between English and French',
  },
];

/**
 * The commands matching what the user has typed after "/", or null when the text is not a
 * bare command fragment. Returning null (rather than an empty list) is what closes the menu the
 * moment the line stops looking like a command, "/refine now do X" is a sentence, not a pick.
 */
export const matchSourceSlashCommands = (
  text: string,
  agentActions: readonly SourceSlashCommand[] = [],
): SourceSlashCommand[] | null => {
  // trimStart, NOT trim: the trailing space is the user saying they are done naming the command.
  // Trimming it kept the menu open on "/interesting ", and while the menu is open Enter PICKS
  // instead of sending - so a command typed in full could never be sent, and picking it from the
  // menu (which types "/name ") left the menu open on top of its own result. Commands meant to be
  // sent bare, like /interesting, were unsendable; the ones people used all had context typed
  // after them, which is what hid this.
  const match = /^\/([a-zA-Z_-]*)$/.exec(text.trimStart());
  if (!match) return null;
  const fragment = (match[1] ?? '').toLowerCase();
  // EVERYTHING, from the first keystroke. The shortcuts still come first, so the short human list is
  // what a bare "/" opens on; the actions follow it rather than replacing it. Hiding them until a
  // character was typed meant a user had to guess a name before being shown the names.
  const pool = [...SOURCE_SLASH_COMMANDS, ...agentActions];
  const hits = pool.filter((command) => command.name.toLowerCase().startsWith(fragment));
  return hits.length > 0 ? hits : null;
};

/**
 * Is this line one of OUR commands rather than a manual agent-action invocation?
 *
 * The composer's "/" path normally dispatches a single COMMAND_DEFINITIONS action and stops.
 * These are not that: they are instructions for the agent, which must go through a normal turn so
 * the goal loop and its narration happen. Without this check "/refine" would be looked up as an
 * action type and rejected as "Unknown manual command: /REFINE".
 *
 * THE MENU AND THIS ARE NOT THE SAME LIST, and that separation is load-bearing. The menu grows -
 * it is meant to, so a user can find what the tool can do without reading the source - and the
 * moment it grows to include the agent's own actions, an entry named like one of them would come
 * through here and be routed as prose. `/QUERY entity:Brand` typed by an operator would stop
 * dispatching QUERY and start being narrated at. So a name that IS an action type never routes
 * here, whatever any menu says, and the menu below builds on top rather than feeding back in.
 */
export const isSourceSlashCommandText = (text: string, agentActionNames: readonly string[] = []): boolean => {
  const match = /^\/([a-zA-Z_-]+)(\s|$)/.exec(text.trim());
  if (!match) return false;
  const name = (match[1] ?? '').toLowerCase();
  if (isAgentActionName(name, agentActionNames)) return false;
  return SOURCE_SLASH_COMMANDS.some((command) => command.name === name);
};

/**
 * Is this name one of the agent's own actions rather than a shortcut of ours?
 *
 * Case-insensitive, because an operator types `/QUERY entity:Brand` in caps and the menu lists
 * things in lower case. This is the guard that lets the menu below grow without ever changing what
 * a typed line DOES.
 */
/** ENRICH_BRAND and enrich-brand are the same name written two ways; both must be recognised. */
const canonicalActionName = (raw: string): string => raw.toLowerCase().replace(/-/g, '_');

const isAgentActionName = (name: string, agentActionNames: readonly string[]): boolean =>
  agentActionNames.some((actionType) => canonicalActionName(actionType) === canonicalActionName(name));

/**
 * THE MENU: our shortcuts first, then everything the agent can actually do.
 *
 * The shortcuts are the answer to "what do I ask for", written for a leasing manager. The actions
 * after them are the answer to "what can this thing do at all", which used to require reading the
 * source: EXTEND_SHORTLIST had worked for months and was invisible, and so is every other action.
 * They are listed last and only match once the user has typed something, so the bare "/" still
 * opens on the short human list rather than a hundred-line debug panel.
 *
 * Picking an action types its name in the caps it is declared in, because that is the form the
 * composer dispatches directly, and its parameters follow as the user types them.
 */
export const buildAgentActionMenu = (
  definitions: Record<string, { action_type: string; description?: string; availableInModes?: readonly string[] }>,
  mode = 'commander',
): SourceSlashCommand[] =>
  Object.values(definitions)
    .filter((definition) => definition.availableInModes?.includes(mode))
    .map((definition) => ({
      group: 'agent' as const,
      // Written like every other entry in this menu: lower case, dashed. ENRICH_BRAND is a registry
      // key, not something to put in front of a leasing manager next to "/refine" and "/export".
      name: definition.action_type.toLowerCase().replace(/_/g, '-'),
      hint: (definition.description ?? '').split(/(?<=\.)\s/)[0]?.slice(0, 110) ?? '',
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
